/**
 * Topnav search input + live typeahead dropdown (P67 + P68).
 *
 * Two parallel UX paths:
 *   1. Press Enter (no active dropdown row) -> navigate to
 *      /search?q=... (P67 baseline).
 *   2. Type 2+ characters -> debounced /v1/search call with
 *      limit=7 -> floating dropdown under the input. Arrow keys
 *      cycle the active row; Enter on an active row navigates to
 *      its surface_url; Escape closes; click-outside closes; an
 *      always-visible "Open all results" footer falls back to the
 *      dedicated /search route.
 *
 * Accessibility: WAI-ARIA combobox 1.2 shape -- combobox on the
 * input, listbox on the panel, option on each row, aria-
 * activedescendant pointing at the keyboard cursor.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { HighlightedText } from "shared/search/HighlightedText";
import {
  clearRecentSearches,
  pushRecentSearch,
  readRecentSearches,
} from "shared/search/recentSearches";
import { useSearch, type SearchHit } from "shared/search/useSearch";

import { Arrow } from "shared/components/Arrow";
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const MAX_DROPDOWN_HITS = 7;

const KIND_LABEL_SHORT: Record<string, string> = {
  // E7: the framework surfaces.
  framework_control: "control",
  cap: "CAP",
  regulator_letter: "letter",
  // Round 20 H58
  person: "Person",
  ibs_service: "IBS",
  // Round 17 L6 -- a function, named as one.
  internal_function: "Function",
  service: "Service",
  capability: "Capability",
  vendor: "Vendor",
  product: "Product",
  configuration_item: "CI",
  channel: "Channel",
  journey: "Journey",
  risk_appetite_statement: "Risk appetite",
  finding: "Finding",
  evidence_artefact: "Evidence",
};

interface TypeaheadDropdownProps {
  /** Trimmed debounced query (already >= MIN_QUERY_LENGTH). */
  query: string;
  /** Index of the active row, or null for none. */
  activeIndex: number | null;
  /** Called when the mouse hovers a row index. */
  onHoverIndex: (index: number) => void;
  /** Called when the operator clicks a row. */
  onSelectHit: (hit: SearchHit) => void;
  /** Called when the "Open all results" footer is clicked. */
  onOpenAll: () => void;
  /** id of the listbox -- referenced by combobox + activedescendant. */
  listboxId: string;
  /** id-prefix for the per-row aria id (combined with index). */
  optionIdPrefix: string;
}

function TypeaheadDropdown({
  query,
  activeIndex,
  onHoverIndex,
  onSelectHit,
  onOpenAll,
  listboxId,
  optionIdPrefix,
}: TypeaheadDropdownProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSearch({
    q: query,
    limit: MAX_DROPDOWN_HITS,
  });
  const hits = data?.results ?? [];

  return (
    <div
      data-testid="topnav-search-dropdown"
      className={[
        "absolute top-full mt-1 start-0 w-96",
        "rounded-md border border-slate-200 bg-white shadow-lg",
        "text-slate-900 overflow-hidden z-50",
      ].join(" ")}
    >
      {isLoading && (
        <div
          data-testid="topnav-search-loading"
          className="px-3 py-2 text-xs text-slate-500"
        >
          {t("home.search.typeaheadLoading")}
        </div>
      )}

      {!isLoading && isError && (
        <div
          data-testid="topnav-search-typeahead-error"
          className="px-3 py-2 text-xs text-rose-700"
        >
          {t("home.search.typeaheadError")}
        </div>
      )}

      {!isLoading && !isError && hits.length === 0 && (
        <div
          data-testid="topnav-search-no-hits"
          className="px-3 py-2 text-xs text-slate-500"
        >
          {t("home.search.typeaheadNoHits")}
        </div>
      )}

      {hits.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t("home.search.typeaheadListboxLabel")}
          className="divide-y divide-slate-100 max-h-80 overflow-y-auto"
        >
          {hits.map((hit, idx) => {
            const isActive = idx === activeIndex;
            const optionId = `${optionIdPrefix}-${idx}`;
            return (
              <li
                id={optionId}
                key={`${hit.kind}:${hit.id}`}
                role="option"
                aria-selected={isActive}
                data-testid={`topnav-search-row-${hit.kind}-${hit.id}`}
                data-active={isActive ? "true" : "false"}
                onMouseEnter={() => onHoverIndex(idx)}
                onMouseDown={(e) => {
                  // mousedown (not click) so we fire before the
                  // outside-click handler dismisses the panel.
                  e.preventDefault();
                  onSelectHit(hit);
                }}
                className={[
                  "px-3 py-2 cursor-pointer",
                  isActive ? "bg-slate-100" : "hover:bg-slate-50",
                ].join(" ")}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">
                    <HighlightedText text={hit.display_name} query={query} />
                    {hit.external && (
                      <span
                        aria-label="opens in new tab"
                        className="ms-1 text-slate-400"
                      >
                        <Arrow kind="up" />
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
                    {KIND_LABEL_SHORT[hit.kind] ?? hit.kind}
                  </span>
                </div>
                {hit.snippet && (
                  <p className="text-xs text-slate-500 truncate">
                    <HighlightedText text={hit.snippet} query={query} />
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        data-testid="topnav-search-open-all"
        onMouseDown={(e) => {
          e.preventDefault();
          onOpenAll();
        }}
        className={[
          "w-full text-start px-3 py-2 text-xs",
          "border-t border-slate-200 bg-slate-50 text-slate-700",
          "hover:bg-slate-100 hover:text-slate-900",
        ].join(" ")}
      >
        {t("home.search.typeaheadOpenAll", { query })}
      </button>
    </div>
  );
}

export function TopnavSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initial = searchParams.get("q") ?? "";

  const [value, setValue] = useState(initial);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLFormElement | null>(null);
  const [recents, setRecents] = useState<string[]>([]);

  const trimmed = value.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const eligible = debounced.length >= MIN_QUERY_LENGTH;
  const dropdownQuery = eligible ? debounced : "";

  // Read the cached results synchronously to know the upper bound
  // for keyboard cursor cycling. The dropdown also reads via
  // useSearch but rendering happens inside; cursor math has to
  // happen up here too. enabled=false when dropdownQuery is empty.
  const { data: cached } = useSearch({
    q: dropdownQuery,
    limit: MAX_DROPDOWN_HITS,
  });
  const hits = cached?.results ?? [];

  const listboxId = useId();
  const optionIdPrefix = useId();
  const activeDescendant =
    activeIndex !== null && hits[activeIndex]
      ? `${optionIdPrefix}-${activeIndex}`
      : undefined;

  // Reset active row whenever the query string changes -- the
  // previously-active index might no longer correspond to the
  // same hit after a new request lands.
  useEffect(() => {
    setActiveIndex(null);
  }, [dropdownQuery]);

  // Hydrate recents from localStorage when the panel opens with
  // an empty input. Re-read on every focus so cross-tab updates
  // land.
  useEffect(() => {
    if (open && trimmed.length === 0) {
      setRecents(readRecentSearches());
    }
  }, [open, trimmed]);

  // Outside-click closes. mousedown so we fire BEFORE any inner
  // click-handler runs.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const goToHit = useCallback(
    (hit: SearchHit) => {
      setOpen(false);
      // The query that produced this hit is the trimmed input;
      // record it as a recent so the operator can re-run it
      // (the typeahead may have already filtered the panel out
      // when the value cleared, but the click was on a live hit).
      if (trimmed) pushRecentSearch(trimmed);
      if (hit.external) {
        // P69: artefact rows open in a new tab; the parent SPA
        // stays on whatever page the operator was on.
        window.open(hit.surface_url, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(hit.surface_url);
    },
    [navigate, trimmed],
  );

  const goToAllResults = useCallback(() => {
    if (!trimmed) return;
    setOpen(false);
    pushRecentSearch(trimmed);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }, [navigate, trimmed]);

  const onPickRecent = useCallback((q: string) => {
    setValue(q);
    setOpen(true);
  }, []);

  const onClearRecents = useCallback(() => {
    clearRecentSearches();
    setRecents([]);
  }, []);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (activeIndex !== null && hits[activeIndex]) {
      goToHit(hits[activeIndex]);
      return;
    }
    if (!trimmed) return;
    goToAllResults();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(null);
      return;
    }
    if (!open || hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        if (prev === null) return 0;
        return Math.min(prev + 1, hits.length - 1);
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        if (prev === null) return hits.length - 1;
        return Math.max(prev - 1, 0);
      });
    }
  };

  const shouldShowDropdown = open && eligible;
  const shouldShowRecents = open && !eligible && recents.length > 0;

  const ariaProps = useMemo(
    () =>
      shouldShowDropdown
        ? {
            "aria-expanded": true as const,
            "aria-controls": listboxId,
            "aria-activedescendant": activeDescendant,
          }
        : { "aria-expanded": false as const },
    [shouldShowDropdown, listboxId, activeDescendant],
  );

  return (
    <form
      ref={containerRef}
      onSubmit={onSubmit}
      data-testid="topnav-search-form"
      className="relative w-full max-w-xs min-w-0"
      role="search"
    >
      <input
        dir="auto"
        type="search"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={t("home.search.placeholder")}
        aria-label={t("home.search.ariaLabel")}
        role="combobox"
        aria-autocomplete="list"
        {...ariaProps}
        data-testid="topnav-search-input"
        autoComplete="off"
        className={[
          "w-full ps-3 pe-9 py-[7px] rounded-[7px] text-xs",
          "bg-brand-navy2 text-sky-text placeholder:text-[#6E7A99]",
          "border border-brand-navyLine focus:border-brand-brass",
          "focus:outline-none focus:ring-1 focus:ring-brand-brass",
        ].join(" ")}
      />
      <button
        type="submit"
        aria-label={t("home.search.submitLabel")}
        data-testid="topnav-search-submit"
        className={[
          "absolute end-2 top-1/2 -translate-y-1/2",
          "text-slate-400 hover:text-brand-brass p-0.5",
        ].join(" ")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m21 21-4.34-4.34M17 11a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z"
          />
        </svg>
      </button>

      {shouldShowDropdown && (
        <TypeaheadDropdown
          query={dropdownQuery}
          activeIndex={activeIndex}
          onHoverIndex={setActiveIndex}
          onSelectHit={goToHit}
          onOpenAll={goToAllResults}
          listboxId={listboxId}
          optionIdPrefix={optionIdPrefix}
        />
      )}

      {shouldShowRecents && (
        <RecentSearchesPanel
          recents={recents}
          onPick={onPickRecent}
          onClear={onClearRecents}
        />
      )}
    </form>
  );
}

interface RecentSearchesPanelProps {
  recents: string[];
  onPick: (q: string) => void;
  onClear: () => void;
}

function RecentSearchesPanel({ recents, onPick, onClear }: RecentSearchesPanelProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="topnav-search-recents"
      className={[
        "absolute top-full mt-1 start-0 w-96",
        "rounded-md border border-slate-200 bg-white shadow-lg",
        "text-slate-900 overflow-hidden z-50",
      ].join(" ")}
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
        {t("home.search.recentSearchesHeading")}
      </div>
      <ul className="divide-y divide-slate-100">
        {recents.map((q) => (
          <li key={q}>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(q);
              }}
              data-testid={`topnav-search-recent-${q}`}
              className="block w-full text-start px-3 py-2 text-sm text-slate-900 hover:bg-slate-50"
            >
              {q}
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          onClear();
        }}
        data-testid="topnav-search-recents-clear"
        className={[
          "w-full text-start px-3 py-2 text-xs",
          "border-t border-slate-200 bg-slate-50 text-slate-700",
          "hover:bg-slate-100 hover:text-slate-900",
        ].join(" ")}
      >
        {t("home.search.recentSearchesClear")}
      </button>
    </div>
  );
}
